import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add, Edit, Delete, Circle, LocationOn } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { 
  addAgitatorMixerToSite, 
  getSiteWithInstruments, 
  updateAgitatorMixerInSite, 
  deleteAgitatorMixerFromSite,
  getTagNamesByProjectId,
  getTelemetryData,
  logActivity
} from '../../api/apiService';
import { TELEMETRY_LIVE_POLL_MS } from '../../constants/telemetry';

const AgitatorsMixersTab = ({ projectId, siteData }) => {
  const userRole = useSelector((state) => state.auth.user?.role);
  const currentUser = useSelector((state) => state.auth.user);
  const isAdminOrManager = userRole === 'Admin' || userRole === 'Manager';

  // State for agitators/mixers data
  const [agitatorsMixers, setAgitatorsMixers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [telemetryData, setTelemetryData] = useState({});
  const [isLoadingTelemetry, setIsLoadingTelemetry] = useState(false);

  // State for tag names
  const [tagNames, setTagNames] = useState([]);
  const [loadingTagNames, setLoadingTagNames] = useState(false);

  // State for dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedAgitatorMixer, setSelectedAgitatorMixer] = useState(null);
  const [formData, setFormData] = useState({
    equipmentDescription: '',
    equipmentLocation: '',
    equipmentType: '',
    onOffTagName: '',
    tripTagName: ''
  });

  // Fetch agitators/mixers data when component mounts or projectId changes
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        const fullSiteData = await getSiteWithInstruments(projectId);
        setAgitatorsMixers(fullSiteData.agitatorsMixers || []);
      } catch (error) {
        console.error('Error fetching site data:', error);
        setAgitatorsMixers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  // Fetch tag names from omsTelemetryLive table
  useEffect(() => {
    const fetchTagNames = async () => {
      if (!projectId) return;
      
      try {
        setLoadingTagNames(true);
        const names = await getTagNamesByProjectId(projectId);
        setTagNames(names);
      } catch (error) {
        console.error('Error fetching tag names:', error);
        setTagNames([]);
      } finally {
        setLoadingTagNames(false);
      }
    };

    fetchTagNames();
  }, [projectId]);

  // Fetch telemetry data for agitators/mixers
  useEffect(() => {
    const fetchTelemetryData = async () => {
      if (!projectId || !agitatorsMixers.length) return;

      try {
        setIsLoadingTelemetry(true);
        const tagIds = [];
        
        // Collect all tag IDs from agitators/mixers
        agitatorsMixers.forEach(equipment => {
          if (equipment.onOffTagName) {
            // Format tagId from tagName: PRJ1255_PLC1:DO_301
            const serverName = siteData?.serverName || 'PLC1';
            const cleanServerName = serverName.replace(`${projectId}_`, '');
            const formattedTagId = `${projectId}_${cleanServerName}:${equipment.onOffTagName}`;
            tagIds.push(formattedTagId);
          }
          if (equipment.tripTagName) {
            // Format tagId from tagName: PRJ1255_PLC1:DI_401
            const serverName = siteData?.serverName || 'PLC1';
            const cleanServerName = serverName.replace(`${projectId}_`, '');
            const formattedTagId = `${projectId}_${cleanServerName}:${equipment.tripTagName}`;
            tagIds.push(formattedTagId);
          }
        });

        if (tagIds.length === 0) return;

        console.log('[AgitatorsMixersTab] Fetching telemetry for tagIds:', tagIds);
        const data = await getTelemetryData(projectId, tagIds);
        
        // Create a map of tagId to value
        const telemetryMap = {};
        data.forEach(item => {
          telemetryMap[item.tagId] = {
            value: item.value,
            timestamp: item.timestamp,
            localTime: item.localTime,
            deviceLocalTime: item.deviceLocalTime
          };
        });
        
        setTelemetryData(telemetryMap);
      } catch (error) {
        console.error('[AgitatorsMixersTab] Error fetching telemetry data:', error);
      } finally {
        setIsLoadingTelemetry(false);
      }
    };

    // Initial fetch
    fetchTelemetryData();

    const intervalId = setInterval(fetchTelemetryData, TELEMETRY_LIVE_POLL_MS);

    return () => clearInterval(intervalId);
  }, [projectId, agitatorsMixers, siteData]);

  // Handle form data change
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    const newAgitatorMixer = {
      id: Date.now(),
      equipmentDescription: formData.equipmentDescription,
      equipmentLocation: formData.equipmentLocation,
      equipmentType: formData.equipmentType,
      onOffTagName: formData.onOffTagName,
      tripTagName: formData.tripTagName,
    };

    try {
      await addAgitatorMixerToSite(projectId, newAgitatorMixer);
      console.log('New agitator/mixer added successfully!');
      
      // Log add agitator/mixer activity to DynamoDB
      try {
        await logActivity(currentUser.email, `Added ${formData.equipmentType}: ${newAgitatorMixer.equipmentDescription}`);
      } catch (err) {
        console.error('Error logging activity to DynamoDB:', err);
      }
      
      const fullSiteData = await getSiteWithInstruments(projectId);
      setAgitatorsMixers(fullSiteData.agitatorsMixers || []);
      setOpenDialog(false);
      setFormData({
        equipmentDescription: '',
        equipmentLocation: '',
        equipmentType: '',
        onOffTagName: '',
        tripTagName: ''
      });
    } catch (error) {
      console.error('Error adding agitator/mixer to DynamoDB:', error);
    }
  };

  // Handle Edit
  const handleEdit = (agitatorMixer) => {
    setSelectedAgitatorMixer(agitatorMixer);
    setFormData({
      equipmentDescription: agitatorMixer.equipmentDescription || '',
      equipmentLocation: agitatorMixer.equipmentLocation || '',
      equipmentType: agitatorMixer.equipmentType || '',
      onOffTagName: agitatorMixer.onOffTagName || '',
      tripTagName: agitatorMixer.tripTagName || '',
    });
    setOpenEditDialog(true);
  };

  // Handle Delete
  const handleDelete = (agitatorMixer) => {
    setSelectedAgitatorMixer(agitatorMixer);
    setOpenDeleteDialog(true);
  };

  // Handle Edit Submit
  const handleEditSubmit = async () => {
    try {
      const updatedAgitatorMixer = {
        ...selectedAgitatorMixer,
        ...formData,
      };
      
      // Ensure we're not creating a new ID
      if (!updatedAgitatorMixer.id) {
        updatedAgitatorMixer.id = selectedAgitatorMixer.id;
      }

      if (userRole === 'Admin' || userRole === 'Manager') {
        await updateAgitatorMixerInSite(projectId, updatedAgitatorMixer);
        // Log edit agitator/mixer activity
        try {
          await logActivity(currentUser.email, `Edited ${updatedAgitatorMixer.equipmentType}: ${updatedAgitatorMixer.equipmentDescription}`);
        } catch (err) {
          console.error('Error logging edit agitator/mixer activity to DynamoDB:', err);
        }
      }
      // Update local state for all roles
      const fullSiteData = await getSiteWithInstruments(projectId);
      setAgitatorsMixers(fullSiteData.agitatorsMixers || []);
      
      // Close dialog and reset state
      setOpenEditDialog(false);
      setSelectedAgitatorMixer(null);
      setFormData({
        equipmentDescription: '',
        equipmentLocation: '',
        equipmentType: '',
        onOffTagName: '',
        tripTagName: ''
      });
    } catch (error) {
      console.error('Error updating agitator/mixer:', error);
    }
  };

  // Handle Delete Confirm
  const handleDeleteConfirm = async () => {
    try {
      await deleteAgitatorMixerFromSite(projectId, selectedAgitatorMixer.id);
      // Log delete agitator/mixer activity
      try {
        await logActivity(currentUser.email, `Deleted ${selectedAgitatorMixer.equipmentType}: ${selectedAgitatorMixer.equipmentDescription}`);
      } catch (err) {
        console.error('Error logging delete agitator/mixer activity to DynamoDB:', err);
      }
      console.log('Agitator/Mixer deleted successfully!');
      const fullSiteData = await getSiteWithInstruments(projectId);
      setAgitatorsMixers(fullSiteData.agitatorsMixers || []);
      setOpenDeleteDialog(false);
      setSelectedAgitatorMixer(null);
    } catch (error) {
      console.error('Error deleting agitator/mixer:', error);
    }
  };

  // Get agitator/mixer status based on telemetry data
  const getAgitatorMixerStatus = (equipment) => {
    if (!equipment.onOffTagName) {
      return 'Unknown';
    }
    
    // Format tagId from tagName for telemetry lookup
    const serverName = siteData?.serverName || 'PLC1';
    const cleanServerName = serverName.replace(`${projectId}_`, '');
    const onOffTagId = `${projectId}_${cleanServerName}:${equipment.onOffTagName}`;
    
    if (!telemetryData[onOffTagId]) {
      return 'Unknown';
    }
    
    const onOffValue = telemetryData[onOffTagId].value;
    
    // Check for trip condition first
    if (equipment.tripTagName) {
      const tripTagId = `${projectId}_${cleanServerName}:${equipment.tripTagName}`;
      if (telemetryData[tripTagId]) {
        const tripValue = telemetryData[tripTagId].value;
        if (tripValue === '1' || tripValue === 1 || tripValue === 'true' || tripValue === true) {
          return 'Trip';
        }
      }
    }
    
    // Check on/off status
    if (onOffValue === '1' || onOffValue === 1 || onOffValue === 'true' || onOffValue === true) {
      return 'On';
    } else if (onOffValue === '0' || onOffValue === 0 || onOffValue === 'false' || onOffValue === false) {
      return 'Off';
    }
    
    return 'Unknown';
  };

  // Get status indicator component
  const getStatusIndicator = (status) => {
    const isOn = status === 'On';
    const isOff = status === 'Off';
    const isTrip = status === 'Trip';
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* On Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isOn ? theme.palette.status.normalGreen : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isOn ? theme.palette.status.normalGreen : theme.palette.grey[500], 
            fontWeight: isOn ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            ON
          </Typography>
        </Box>
        
        {/* Off Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isOff ? theme.palette.status.alarmRed : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isOff ? theme.palette.status.alarmRed : theme.palette.grey[500], 
            fontWeight: isOff ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            OFF
          </Typography>
        </Box>
        
        {/* Trip Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isTrip ? theme.palette.status.warningAmber : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isTrip ? theme.palette.status.warningAmber : theme.palette.grey[500], 
            fontWeight: isTrip ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            TRIP
          </Typography>
        </Box>
      </Box>
    );
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress sx={{ color: (theme) => theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: { xs: '300px', sm: '350px', md: '400px' },
      overflow: 'auto',
      position: 'relative'
    }}>
      <Stack spacing={2}>
        {agitatorsMixers.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            color: (theme) => theme.palette.text.secondary
          }}>
            <Typography variant="body1">No agitators/mixers found. Add your first equipment using the + button.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {agitatorsMixers.map((equipment, index) => {
              const status = getAgitatorMixerStatus(equipment);
              
              return (
                <Card key={equipment.id || index} sx={{ 
                  borderRadius: '16px', 
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 12px rgba(0,0,0,0.08)',
                  background: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 10px 28px rgba(0,0,0,0.45)' : '0 8px 25px rgba(0,0,0,0.15)',
                    border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(25, 118, 210, 0.2)',
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.1rem',
                        color: (theme) => theme.palette.text.primary,
                        lineHeight: 1.3
                      }}>
                        {equipment.equipmentDescription}
                      </Typography>
                      {isAdminOrManager && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit Equipment">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(equipment);
                              }}
                              sx={{
                                color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Equipment">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(equipment);
                              }}
                              sx={{
                                color: (theme) => theme.palette.error.main,
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                    <Stack spacing={2}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 1.5,
                        backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
                        borderRadius: '8px'
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                          fontSize: '0.875rem'
                        }}>
                          {equipment.equipmentType}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 1.5,
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                        borderRadius: '8px'
                      }}>
                          <Typography variant="body2" sx={{ 
                            color: (theme) => theme.palette.text.secondary,
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <LocationOn sx={{ fontSize: 16, mr: 0.5, color: (theme) => theme.palette.text.secondary }} /> {equipment.equipmentLocation}
                          </Typography>
                      </Box>
                      
                      {/* Status Indicators */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        p: 2,
                        backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                        borderRadius: '8px'
                      }}>
                        {isLoadingTelemetry ? (
                          <CircularProgress size={20} sx={{ color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main }} />
                        ) : (
                          getStatusIndicator(status)
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Stack>
      
      {isAdminOrManager && (
        <Fab
          color="primary"
          aria-label="add agitator/mixer"
          onClick={() => setOpenDialog(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            backgroundColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
            color: (theme) => theme.palette.getContrastText(theme.palette.brand?.royalBlue || theme.palette.primary.main),
            '&:hover': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' 
                ? alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.85)
                : theme.palette.brand?.royalBlue || theme.palette.primary.main,
              filter: (theme) => theme.palette.mode === 'dark' ? 'none' : 'brightness(0.95)',
            },
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Add Agitator/Mixer Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: "12px", 
            padding: {
              xs: "12px",
              sm: "16px",
              md: "20px"
            },
            boxShadow: "0px 8px 24px rgba(0,0,0,0.15)"
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: "600", 
          color: "#1A1F36",
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: {
            xs: '1.1rem',
            sm: '1.25rem',
            md: '1.5rem'
          }
        }}>
          Add New Agitator/Mixer
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: "16px" }}>
            <TextField
              fullWidth
              label="Equipment Description"
              name="equipmentDescription"
              value={formData.equipmentDescription}
              onChange={handleFormChange}
              variant="outlined"
              required
            />
            <TextField
              fullWidth
              label="Equipment Location"
              name="equipmentLocation"
              value={formData.equipmentLocation}
              onChange={handleFormChange}
              variant="outlined"
              required
            />
            <FormControl fullWidth>
              <InputLabel id="equipment-type-label">Equipment Type</InputLabel>
              <Select
                labelId="equipment-type-label"
                label="Equipment Type"
                name="equipmentType"
                value={formData.equipmentType}
                onChange={handleFormChange}
                variant="outlined"
                required
              >
                <MenuItem value="">Select Equipment Type</MenuItem>
                <MenuItem value="Agitator">Agitator</MenuItem>
                <MenuItem value="Mixer">Mixer</MenuItem>
                <MenuItem value="Stirrer">Stirrer</MenuItem>
                <MenuItem value="Settler">Settler</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="on-off-tag-label">On/Off Tag Name</InputLabel>
              <Select
                labelId="on-off-tag-label"
                label="On/Off Tag Name"
                name="onOffTagName"
                value={formData.onOffTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
                required
              >
                <MenuItem value="">Select an On/Off Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="trip-tag-label">Trip Tag Name</InputLabel>
              <Select
                labelId="trip-tag-label"
                label="Trip Tag Name"
                name="tripTagName"
                value={formData.tripTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select a Trip Tag Name (Optional)</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ padding: "16px" }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            color="secondary" 
            sx={{ 
              textTransform: "none",
              color: '#666',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary" 
            sx={{ 
              textTransform: "none", 
              boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Agitator/Mixer Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Edit Agitator/Mixer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: '16px' }}>
            <TextField
              fullWidth
              label="Equipment Description"
              name="equipmentDescription"
              value={formData.equipmentDescription}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Equipment Location"
              name="equipmentLocation"
              value={formData.equipmentLocation}
              onChange={handleFormChange}
              variant="outlined"
            />
            <FormControl fullWidth>
              <InputLabel id="edit-equipment-type-label">Equipment Type</InputLabel>
              <Select
                labelId="edit-equipment-type-label"
                label="Equipment Type"
                name="equipmentType"
                value={formData.equipmentType}
                onChange={handleFormChange}
                variant="outlined"
              >
                <MenuItem value="">Select Equipment Type</MenuItem>
                <MenuItem value="Agitator">Agitator</MenuItem>
                <MenuItem value="Mixer">Mixer</MenuItem>
                <MenuItem value="Stirrer">Stirrer</MenuItem>
                <MenuItem value="Settler">Settler</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="edit-on-off-tag-label">On/Off Tag Name</InputLabel>
              <Select
                labelId="edit-on-off-tag-label"
                label="On/Off Tag Name"
                name="onOffTagName"
                value={formData.onOffTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select an On/Off Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="edit-trip-tag-label">Trip Tag Name</InputLabel>
              <Select
                labelId="edit-trip-tag-label"
                label="Trip Tag Name"
                name="tripTagName"
                value={formData.tripTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select a Trip Tag Name (Optional)</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            color="primary"
          >
            Submit
          </Button>
          <Button 
            onClick={() => setOpenEditDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Agitator/Mixer Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Delete Agitator/Mixer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this equipment?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="primary"
          >
            Delete
          </Button>
          <Button 
            onClick={() => setOpenDeleteDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgitatorsMixersTab; 